import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, DollarSign, CheckCircle2, XCircle } from "lucide-react";
import { trackApplyClick } from "@/lib/apply-cta";

const URL = "https://vendingmachinefree.com/vending-machine-cost";
const TITLE = "How Much Does a Vending Machine Cost? (2026 US Pricing Guide)";
const DESC =
  "New vending machines cost $3,000–$10,000+ to buy. See a full 2026 price breakdown by type — snack, drink, combo, coffee, food, ice — and how to get one placed FREE at your US business.";

const PRICING = [
  { type: "Snack Machine", buy: "$3,000 – $5,500", free: "FREE" },
  { type: "Drink / Beverage Machine", buy: "$3,500 – $6,000", free: "FREE" },
  { type: "Combo (Snack + Drink)", buy: "$4,500 – $7,500", free: "FREE" },
  { type: "Coffee Machine", buy: "$2,500 – $10,000", free: "FREE" },
  { type: "Fresh Food Machine", buy: "$6,000 – $12,000", free: "FREE" },
  { type: "Ice Machine", buy: "$4,000 – $9,000", free: "FREE" },
];

const FAQ = [
  {
    q: "How much does a vending machine cost in 2026?",
    a: "A new commercial vending machine costs between $3,000 and $12,000 depending on type. Snack machines start around $3,000, combo units run $4,500–$7,500, and refrigerated food machines can exceed $10,000. Refurbished units run 30–50% less.",
  },
  {
    q: "What are the ongoing costs of owning a vending machine?",
    a: "Owners pay for product inventory (35–50% of revenue), electricity ($20–$40/month), maintenance, cash handling, and delivery routes — typically $150–$400/month per machine before restock costs.",
  },
  {
    q: "How can I get a vending machine at no cost?",
    a: "Vending Machine Free.com provides qualifying US businesses with 100% free vending machines. We cover the equipment, delivery, installation, restocking, servicing, and repairs — you provide the space and a standard power outlet.",
  },
  {
    q: "Is a free vending machine really free — no hidden fees?",
    a: "Yes. No purchase price, no lease, no service contract fees, no restocking charges. We generate revenue from product sales; you get free refreshments for staff and visitors.",
  },
  {
    q: "What's the ROI on buying vs getting a free vending machine?",
    a: "Purchased machines typically break even in 12–24 months only if daily traffic supports it. Free placement eliminates capital risk and management overhead entirely — most businesses choose free placement.",
  },
];

export const Route = createFileRoute("/vending-machine-cost")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              headline: TITLE,
              description: DESC,
              author: { "@type": "Organization", name: "Vending Machine Free.com" },
              publisher: {
                "@type": "Organization",
                name: "Vending Machine Free.com",
                url: "https://vendingmachinefree.com",
              },
              mainEntityOfPage: URL,
              datePublished: "2026-07-02",
              dateModified: "2026-07-02",
            },
            {
              "@type": "FAQPage",
              mainEntity: FAQ.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://vendingmachinefree.com/" },
                { "@type": "ListItem", position: 2, name: "Vending Machine Cost", item: URL },
              ],
            },
          ],
        }),
      },
    ],
  }),
  component: CostPage,
});

function CostPage() {
  return (
    <div className="min-h-screen bg-background">
      <section className="bg-charcoal text-charcoal-foreground py-16">
        <div className="container-x max-w-4xl">
          <p className="text-xs uppercase tracking-[0.2em] text-safety">2026 US Pricing Guide</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl uppercase leading-tight">
            How Much Does a Vending Machine Cost?
          </h1>
          <p className="mt-4 text-lg text-white/85 max-w-2xl">
            New commercial vending machines run $3,000–$12,000+. Below is the full 2026 breakdown by
            type — plus how qualifying US businesses can get one placed for $0.
          </p>
          <Link
            to="/"
            hash="contact"
            onClick={() => trackApplyClick("cost_hero")}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety px-6 py-3 font-semibold text-safety-foreground"
          >
            Get One FREE Instead <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <section className="py-16">
        <div className="container-x max-w-5xl">
          <h2 className="font-display text-3xl uppercase">Cost Breakdown by Machine Type</h2>
          <div className="mt-6 overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-b-2 border-charcoal">
                  <th className="p-3 font-display uppercase">Machine Type</th>
                  <th className="p-3 font-display uppercase">Buy New</th>
                  <th className="p-3 font-display uppercase">Via Placement</th>
                </tr>
              </thead>
              <tbody>
                {PRICING.map((p) => (
                  <tr key={p.type} className="border-b border-border">
                    <td className="p-3 font-semibold">{p.type}</td>
                    <td className="p-3">{p.buy}</td>
                    <td className="p-3 text-safety font-bold">{p.free}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-16 bg-secondary">
        <div className="container-x max-w-5xl grid gap-8 md:grid-cols-2">
          <div className="rounded-lg border border-border bg-background p-6">
            <XCircle className="h-8 w-8 text-destructive" />
            <h3 className="mt-3 font-display text-xl uppercase">Buying Your Own</h3>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li>• $3,000–$12,000 upfront capital</li>
              <li>• You buy & manage product inventory</li>
              <li>• You handle repairs, cash, restocking</li>
              <li>• 12–24 month ROI (if traffic supports it)</li>
              <li>• Electricity, delivery, maintenance costs</li>
            </ul>
          </div>
          <div className="rounded-lg border-2 border-safety bg-background p-6">
            <CheckCircle2 className="h-8 w-8 text-safety" />
            <h3 className="mt-3 font-display text-xl uppercase">Free Placement</h3>
            <ul className="mt-3 space-y-2 text-sm">
              <li>• $0 upfront, $0 monthly, $0 service</li>
              <li>• Fully stocked and maintained for you</li>
              <li>• 4–6 hour response on service calls</li>
              <li>• Modern, branded machines</li>
              <li>• Cancel anytime — no lock-in</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container-x max-w-4xl">
          <h2 className="font-display text-3xl uppercase">FAQ — Vending Machine Costs</h2>
          <div className="mt-6 space-y-4">
            {FAQ.map((f) => (
              <details key={f.q} className="rounded-lg border border-border bg-secondary p-5">
                <summary className="cursor-pointer font-semibold">{f.q}</summary>
                <p className="mt-3 text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-charcoal text-charcoal-foreground">
        <div className="container-x max-w-4xl text-center">
          <DollarSign className="mx-auto h-10 w-10 text-safety" />
          <h2 className="mt-3 font-display text-3xl uppercase">Skip the $10,000 Price Tag</h2>
          <p className="mt-3 text-white/85">
            Qualifying US businesses get a modern, fully stocked vending machine — free.
          </p>
          <Link
            to="/"
            hash="contact"
            onClick={() => trackApplyClick("cost_footer")}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety px-6 py-3 font-semibold text-safety-foreground"
          >
            Apply Now <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="mt-8 text-sm text-white/70">
            Powered by American businesses — get a Free Vending Machine Nationwide.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-4 text-safety text-sm">
            <a className="hover:underline" href="/qualify-for-free-vending-machine">Qualification Requirements</a>
            <a className="hover:underline" href="/free-vending-machines-usa">Free Vending USA</a>
            <a className="hover:underline" href="/free-vending-machine-placement">Free Placement</a>
            <Link className="hover:underline" to="/coverage">USA Coverage</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
